import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Loader2, PhoneCall } from "lucide-react";
import { TIME_SLOTS, CONSULT_TYPES } from "@/data/site";
import { SECTION, BTN_GOLD, BTN_PRIMARY, INPUT_CLS, INPUT_ON_NAVY, API } from "./styles";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const initial = { name: "", email: "", phone: "", notes: "" };

export const Consultation = () => {
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);

  const [cb, setCb] = useState({ name: "", phone: "", best_time: "", notes: "" });
  const [cbLoading, setCbLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !slot || !type) {
      toast.error("Please choose a date, time slot and consultation type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, {
        ...form,
        date: format(date, "EEEE, MMMM d, yyyy"),
        time_slot: slot,
        consult_type: type,
      });
      toast.success("Consultation booked — we'll confirm your appointment shortly.");
      setForm(initial);
      setDate(null);
      setSlot("");
      setType("");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const submitCallback = async (e) => {
    e.preventDefault();
    if (!cb.best_time) {
      toast.error("Please choose the best time to call you.");
      return;
    }
    setCbLoading(true);
    try {
      await axios.post(`${API}/callbacks`, cb);
      toast.success("Callback requested — we'll call you back soon.");
      setCb({ name: "", phone: "", best_time: "", notes: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setCbLoading(false);
    }
  };

  return (
    <section id="consultation" data-testid="consultation-section" className={`${SECTION} bg-white`}>
      <div className="mb-16">
        <p className="font-accent-script text-2xl md:text-3xl text-[#D4AF37] mb-3">Meet With Us</p>
        <h2 className="font-serif text-3xl md:text-5xl font-semibold text-[#0A1F3C] gold-underline"
          data-testid="consultation-headline">
          SCHEDULE A CONSULTATION
        </h2>
        <p className="text-[#5A6A85] mt-6 max-w-2xl leading-relaxed">
          Pick a date and time that works for you — on-site, by phone, or virtual. Prefer we reach
          out first? Request a callback instead.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 bg-[#F7F7F4] border border-[#E2E2DE] p-8 md:p-10 space-y-8"
          data-testid="consultation-form">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Full Name *</label>
              <input required data-testid="consultation-name-input" className={INPUT_CLS} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Email *</label>
              <input required type="email" data-testid="consultation-email-input" className={INPUT_CLS} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Phone *</label>
              <input required type="tel" data-testid="consultation-phone-input" className={INPUT_CLS} value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(804) 555-0123" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-2">Preferred Date *</label>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="consultation-date-trigger"
                    className="flex items-center justify-between w-full bg-transparent border-b border-[#C9C9C4] text-[#0A1F3C] py-3 px-2 text-sm hover:border-[#D4AF37] transition-colors duration-300">
                    <span className={date ? "text-[#0A1F3C]" : "text-gray-400"}>
                      {date ? format(date, "MMM d, yyyy") : "Pick a date"}
                    </span>
                    <CalendarIcon className="h-4 w-4 text-[#D4AF37]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#E2E2DE]" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || d.getDay() === 0}
                    className="text-[#0A1F3C]" data-testid="consultation-calendar" />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-2">Time Slot *</label>
              <Select value={slot} onValueChange={setSlot}>
                <SelectTrigger data-testid="consultation-slot-select"
                  className="bg-transparent border-0 border-b border-[#C9C9C4] rounded-none text-[#0A1F3C] px-2 focus:ring-0">
                  <SelectValue placeholder="Choose a time" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E2DE] text-[#0A1F3C]">
                  {TIME_SLOTS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`consultation-slot-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85] block mb-2">Consultation Type *</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="consultation-type-select"
                  className="bg-transparent border-0 border-b border-[#C9C9C4] rounded-none text-[#0A1F3C] px-2 focus:ring-0">
                  <SelectValue placeholder="Choose a type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E2DE] text-[#0A1F3C]">
                  {CONSULT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`consultation-type-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#5A6A85]">Notes</label>
            <textarea rows={3} data-testid="consultation-notes-input" className={`${INPUT_CLS} resize-none`}
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything we should know before we meet?" />
          </div>

          <button type="submit" disabled={loading} data-testid="consultation-submit-btn"
            className={`${BTN_GOLD} disabled:opacity-60`}>
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Booking..." : "Book a Consultation"}
          </button>
        </form>

        <div className="bg-[#0A1F3C] border border-[#D4AF37]/40 p-8 md:p-10" data-testid="callback-card">
          <PhoneCall className="h-8 w-8 text-[#D4AF37] mb-4" />
          <h3 className="font-serif text-2xl text-white mb-2">Request a Callback</h3>
          <p className="text-sm text-gray-300 mb-8 leading-relaxed">
            Leave your number and the best time to reach you — we'll call you back.
          </p>
          <form onSubmit={submitCallback} className="space-y-6" data-testid="callback-form">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70">Name *</label>
              <input required data-testid="callback-name-input" className={INPUT_ON_NAVY} value={cb.name}
                onChange={(e) => setCb({ ...cb, name: e.target.value })} placeholder="John Smith" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70">Phone *</label>
              <input required type="tel" data-testid="callback-phone-input" className={INPUT_ON_NAVY} value={cb.phone}
                onChange={(e) => setCb({ ...cb, phone: e.target.value })} placeholder="(804) 555-0123" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70 block mb-2">Best Time *</label>
              <Select value={cb.best_time} onValueChange={(v) => setCb({ ...cb, best_time: v })}>
                <SelectTrigger data-testid="callback-time-select"
                  className="bg-transparent border-0 border-b border-white/30 rounded-none text-white px-2 focus:ring-0">
                  <SelectValue placeholder="Morning, afternoon, evening?" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E2E2DE] text-[#0A1F3C]">
                  {["Morning (8am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–7pm)"].map((t) => (
                    <SelectItem key={t} value={t} data-testid={`callback-time-${t.split(" ")[0].toLowerCase()}`}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={cbLoading} data-testid="callback-submit-btn"
              className={`${BTN_PRIMARY} w-full disabled:opacity-60`}>
              {cbLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {cbLoading ? "Sending..." : "Request Callback"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
